/**
 * apps/web :: lib/practice-api
 *
 * Typed wrappers over the `/api/practice/*` surface shipped in PR #111
 * (ROADS-294). Mirrors the fetch-wrapper conventions used by profile-api
 * and rulediff-api — every call is credentialed, 4xx/5xx bubble up as
 * `ApiError`, and 404 responses with `status: "no_questions_available"`
 * surface as `null` on `getNextQuestion` the same way `getProfile` maps
 * `no_profile` to `null`.
 *
 * Wire-shape asymmetry (worth pinning once because it surprises people):
 *
 *   - PracticeQuestionForClient (shared) uses `@SerialName` to force
 *     snake_case for compound fields — `state_code`, `question_text`,
 *     `handbook_section` — and leaves single-word fields camelCase.
 *     The TS interface mirrors that exactly.
 *   - AttemptResult, ProgressSummary, SubmitAttemptRequest (backend-only
 *     DTOs) have NO `@SerialName` annotations, so kotlinx.serialization
 *     emits pure camelCase on the wire. We translate ONCE here into
 *     snake_case so component-level code sees a single convention.
 *   - QuestionCategory ships as its UPPER_SNAKE enum name on the wire
 *     ("RIGHT_OF_WAY"). ProgressSummary.byCategory keys are lowercased
 *     ("right_of_way") per PracticeRoutes.kt:344. The TS types reflect
 *     both shapes verbatim.
 *
 * Any divergence from these shapes is a bug; the contract lives in
 * `backend/src/main/kotlin/cx/humane/roadbridge/practice/PracticeDomain.kt`
 * and `kmp-shared-core/.../practice/PracticeQuestion.kt`.
 */
import { ApiError, apiFetch } from "@/lib/api";

/**
 * Client-safe practice question. Mirrors
 * `cx.humane.roadbridge.shared.practice.PracticeQuestionForClient`
 * verbatim. Omits `correctChoiceId` + `explanation` (Hard Rule #4 in PR
 * #111 — never leaked pre-submission).
 */
export interface PracticeQuestionForClient {
  id: string;
  state_code: string;
  locale: string;
  /** Enum name as wire emits it — UPPER_SNAKE (e.g. "RIGHT_OF_WAY"). */
  category: string;
  question_text: string;
  choices: Array<{ id: string; text: string }>;
  handbook_section: string | null;
  difficulty: number;
}

/**
 * Result of POST /api/practice/attempts. Wire is camelCase; we map to
 * snake_case here so the consuming components don't have to care about
 * the backend DTO annotation policy.
 */
export interface AttemptResult {
  attempt_id: string;
  correct: boolean;
  correct_choice_id: string;
  explanation: string;
  handbook_section: string | null;
}

/** Per-category breakdown inside [PracticeProgress]. */
export interface CategoryProgress {
  attempted: number;
  correct_first_try: number;
  total: number;
}

/** GET /api/practice/progress response. */
export interface PracticeProgress {
  state_code: string;
  locale: string;
  total_available: number;
  total_attempted: number;
  correct_first_try: number;
  current_streak: number;
  by_category: Record<string, CategoryProgress>;
}

/** POST /api/practice/attempts request payload. */
export interface SubmitAttemptPayload {
  question_id: string;
  chosen_choice_id: string;
  time_to_answer_ms: number;
}

/**
 * Fetch the next practice question. Returns `null` on 404
 * `{status:"no_questions_available"}` — the empty-state UI is triggered
 * off that null rather than an exception.
 */
export async function getNextQuestion(
  state: string,
  category?: string,
): Promise<PracticeQuestionForClient | null> {
  const params = new URLSearchParams({ state });
  if (category) params.set("category", category);
  try {
    return await apiFetch<PracticeQuestionForClient>(
      `/api/practice/next?${params.toString()}`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      const body = err.body as { status?: string } | null;
      if (body?.status === "no_questions_available") return null;
    }
    throw err;
  }
}

/**
 * Submit a user's answer. Returns the full attempt result (correct flag +
 * correct_choice_id + explanation + handbook citation). The server rejects
 * malformed payloads with 400 — `ApiError.message` carries the server's
 * human-readable reason.
 *
 * Wire translation: TS consumers hand us snake_case; the backend expects
 * camelCase (`questionId`, `chosenChoiceId`, `timeToAnswerMs`). The wire
 * response is also camelCase; we re-shape to snake_case for the caller.
 */
export async function submitAttempt(
  payload: SubmitAttemptPayload,
): Promise<AttemptResult> {
  const wire = await apiFetch<{
    attemptId: string;
    correct: boolean;
    correctChoiceId: string;
    explanation: string;
    handbookSection: string | null;
  }>("/api/practice/attempts", {
    method: "POST",
    body: JSON.stringify({
      questionId: payload.question_id,
      chosenChoiceId: payload.chosen_choice_id,
      timeToAnswerMs: payload.time_to_answer_ms,
    }),
  });
  return {
    attempt_id: wire.attemptId,
    correct: wire.correct,
    correct_choice_id: wire.correctChoiceId,
    explanation: wire.explanation,
    handbook_section: wire.handbookSection ?? null,
  };
}

/** Fetch aggregated progress for the caller + state. */
export async function getProgress(state: string): Promise<PracticeProgress> {
  const params = new URLSearchParams({ state });
  const wire = await apiFetch<{
    stateCode: string;
    locale: string;
    totalAvailable: number;
    totalAttempted: number;
    correctFirstTry: number;
    currentStreak: number;
    byCategory: Record<
      string,
      { attempted: number; correctFirstTry: number; total: number }
    >;
  }>(`/api/practice/progress?${params.toString()}`);

  const by_category: Record<string, CategoryProgress> = {};
  for (const [k, v] of Object.entries(wire.byCategory ?? {})) {
    by_category[k] = {
      attempted: v.attempted,
      correct_first_try: v.correctFirstTry,
      total: v.total,
    };
  }
  return {
    state_code: wire.stateCode,
    locale: wire.locale,
    total_available: wire.totalAvailable,
    total_attempted: wire.totalAttempted,
    correct_first_try: wire.correctFirstTry,
    current_streak: wire.currentStreak,
    by_category,
  };
}
